import type { ArtistStageEntry, OrderHistory, OrderStage, OrderStatus } from '../types';

/**
 * Одна лента вместо двух. Этап и статус — связанные автоматы: перевод этапа
 * почти всегда двигает и статус, поэтому две истории показывали одно и то же
 * событие дважды, разными словами и в разных карточках.
 *
 * Здесь события одного момента склеиваются в строку, а разные остаются
 * раздельными: художник переводит статус и без смены этапа.
 */
export interface TimelineEntry {
  id: string;
  at: string;
  stage: { from: OrderStage | null; to: OrderStage } | null;
  status: OrderStatus | null;
  publicComment: string | null;
  privateComment: string | null;
  attachments: number;
}

/**
 * Записи одной транзакции расходятся на миллисекунды, поэтому склеиваем по
 * окну, а не по точному равенству. Пять секунд: два независимых перевода
 * подряд человек за это время не делает.
 */
const MERGE_WINDOW_MS = 5000;

export function buildOrderTimeline(
  stageHistory: ArtistStageEntry[],
  statusHistory: OrderHistory[],
): TimelineEntry[] {
  const entries: TimelineEntry[] = stageHistory.map((entry) => ({
    id: entry.id,
    at: entry.createdAt,
    stage: { from: entry.previousStage, to: entry.newStage },
    status: null,
    publicComment: entry.publicComment,
    privateComment: entry.privateComment,
    attachments: entry.attachments.length,
  }));

  for (const entry of statusHistory) {
    const time = Date.parse(entry.createdAt);
    const sibling = entries.find(
      (candidate) =>
        candidate.status === null && Math.abs(Date.parse(candidate.at) - time) <= MERGE_WINDOW_MS,
    );

    if (sibling) {
      sibling.status = entry.newStatus;
      // Комментарий писали один раз — в обеих историях он один и тот же.
      sibling.publicComment ??= entry.publicComment;
      sibling.privateComment ??= entry.privateComment;
      continue;
    }

    entries.push({
      id: entry.id,
      at: entry.createdAt,
      stage: null,
      status: entry.newStatus,
      publicComment: entry.publicComment,
      privateComment: entry.privateComment,
      attachments: 0,
    });
  }

  // Свежее сверху: историю читают с конца, а не с начала работы.
  return entries.sort((left, right) => Date.parse(right.at) - Date.parse(left.at));
}
